import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';

import * as Yup from 'yup';

import Alert from 'antd/es/alert';

import Button from '@common/react/components/Forms/Button';
import {
	dateValidator,
	lengthValidator,
	optionalEmailValidator,
	phoneRegexp,
	phoneValidator,
	simpleStringValidator,
} from '@common/react/utils/validationHelpers';
import ScrollToError from '@common/react/components/Forms/ScrollToError';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { AuthResult } from '@common/react/objects/LoginFormValues';
import ChangePhoneModal from '@common/react/components/UI/ChangePhoneModal/ChangePhoneModal';

import { Gender } from '@commonTuna/react/objects/Enums';
import { BaseInvite } from '@commonTuna/react/objects/BaseInvite';
import { PortalRegistrationStep } from '@commonTuna/react/objects/PortalRegistrationStep';

import { RegistrationUser, User, UserRole } from '@app/objects/User';
import { Suffix } from '@app/objects/Suffix';
import { MarriageStatus } from '@app/objects/MarriageStatus';
import DemographicInfoRegistration from '@app/components/UI/DemographicInfo/DemographicInfoRegistration';
import { PatientLocation } from '@app/objects/PatientLocation';
import { PortalSettings } from '@app/components/Pages/Register/Register';
import { Init } from '@app/objects/Init';

type FormValues = RegistrationUser | User;

interface ComponentProps {
	initDate?: any;
	token: string;
	afterSubmit: (rSteps?: Array<PortalRegistrationStep>, rUser?: User) => void;
}

export interface UserRegistrationSteps {
	steps: Array<PortalRegistrationStep>;
	hipaa: Array<BaseInvite>;
	forms: Array<BaseInvite>;
	user: User;
	portalSettings: PortalSettings;
}

const validationSchema = Yup.object().shape({
	firstName: lengthValidator(50, 'Must be not more than 50 characters').required(),
	lastName: lengthValidator(50, 'Must be not more than 50 characters').required(),
	birthDate: dateValidator,
	// city: simpleStringValidator,
	// region: simpleStringValidator,
	// zip: simpleStringValidator,
	password: simpleStringValidator.min(8).required(),
	repeatPassword: Yup.string().required().oneOf([Yup.ref('password')], 'Repeat password should be the same as password'),
	phoneNumber: Yup.string()
		.when('email', {
			is: (v) => !v,
			then: Yup.string().matches(phoneRegexp, 'Invalid phone number').required('Cell Phone or Email is required'),
			otherwise: phoneValidator,
		}),
	email: Yup.string()
		.when('phoneNumber', {
			is: (v) => !(v),
			then: Yup.string().email().required('Cell Phone or Email is required'),
			otherwise: optionalEmailValidator,
		}),
}, [['email', 'phoneNumber']]);

export const PatientMainForm: React.FC<ComponentProps> = ({
	initDate,
	token,
	afterSubmit,
}) => {
	const {
		handleLogin,
	} = useApplicationContext();

	const [loading, setLoading] = React.useState(false);
	const location = useLocation();
	const data = initDate ?? location?.state?.data;
	const [initialErrors, setInitErrors] = React.useState({});
	const [userPhone, setUserPhone] = React.useState(null);

	const initialValues = {
		id: data?.id || -1,
		role: UserRole.User,
		suffix: data?.gender === Gender.Male ? Suffix.Mr : Suffix.Ms,
		firstName: data?.firstName || '',
		lastName: data?.lastName || '',
		gender: data?.gender ?? Gender.Female,
		birthDate: data?.birthDate || null,
		marriageStatus: MarriageStatus.Single,
		email: data?.email || '',
		phoneNumber: data?.phoneNumber || data?.phone || '',
		password: '',
		repeatPassword: '',
		address: data?.address || '',
		city: data?.city || '',
		region: data?.region || '',
		zip: data?.zip || '',
		emergencyContactName: data?.emergencyContactName || '',
		emergencyContactPhone: data?.emergencyContactPhone || '',
		emergencyContactRelation: data?.emergencyContactRelation || '',
		allowEmail: data?.allowEmail ?? true,
		allowSms: data?.allowSms ?? true,
		allowCalls: data?.allowCalls ?? true,
		allowPush: data?.allowPush ?? true,
		agreementForSignUp: true,
		captcha: '',
		patientLocations: data?.patientLocations || [] as Array<PatientLocation>,
	} as RegistrationUser;

	const showAddress = initialValues.patientLocations.length > 0;

	React.useEffect(() => {
		validationSchema.validate(initialValues, { abortEarly: false })
			.catch((err) => {
				const er = {};
				for (let i = 0; i < err.inner.length; i++) {
					er[err.inner[i].path] = err.inner[i].errors[0];
				}
				setInitErrors(er);
			});
	}, []);

	const onRegistrationSubmit = (res: AuthResult<User, Init>, formValues: any) => {
		const init = res?.initObject;

		if (init) {
			setUserPhone(null);

			handleLogin(init);

			afterSubmit(init.userRegistrationSteps?.steps, init.user);
		} else {
			// if init is null, so this is phoneNumber registration case, show phone code modal
			setUserPhone(formValues?.phoneNumber);
		}
	};

	const renderButtons = () => {
		return <>
			<div className="text-center button-box">
				<Button
					isLoading={loading}
					className="btn btn-component mb10"
				>
					Create Account
				</Button>
			</div>
		</>;
	};

	return (
		<>
			<div className="contact-text mt10">
				<Alert
					message={<>
						{showAddress ? 'Check that the information about you filled in at the clinic is correct.' : <>
							Fill in your details to get started.
							{' '}
							<Link to="/about-registration">Why I need to register?</Link>
						</>}
					</>}
					type="info"
				/>
			</div>
			<ItemProvider<FormValues>
				id={-1}
				saveRequest="userRegistrationAuth"
				add={initialValues}
				validationSchema={validationSchema}
				clearForSubmit={({ item }) => ({
					...item,
					token,
				})}
			>
				<ItemEditor<FormValues, AuthResult<User, Init>>
					resetFormAfterSubmit
					beforeSubmit={({ submit }) => {
						setLoading(true);
						submit()
							.finally(() => setLoading(false));
						return false;
					}}
					formikProps={{
						validateOnMount: true,
						initialErrors,
					}}
					afterSubmit={({ item: formValues, res }) => onRegistrationSubmit(res, formValues)}
					edit={({ formikBag }) => <>
						<ScrollToError {...formikBag} formId="patient-registration-form" />
						<DemographicInfoRegistration showAddress={showAddress} formikBag={formikBag} />
						{renderButtons()}
					</>}
				/>
			</ItemProvider>
			{userPhone !== null && <ChangePhoneModal<User, Init>
				defaultConfirmVisible
				finalAction={(data, res) => {
					onRegistrationSubmit(res, null);
				}}
				savedPhoneNumber={userPhone}
				className="change-info-modal"
				modalOptions={{
					closable: false,
					open: true,
				}}
				path="/registration"
			/>}
		</>
	);
};
